import type { TraceLine } from '../api';

/**
 * Per-turn latency waterfall, built from `turn.latency` events in the log
 * stream itself. That is why this works during a live call — the numbers arrive
 * as the turns happen, rather than being averaged away at the end.
 *
 * Realtime pipelines (Gemini/OpenAI Live) never emit `turn.latency`, because it
 * requires EOU + LLM + TTS metrics for the same speech id. They emit
 * `turn.realtime` / `turn.measured_latency` instead, which this also renders.
 */

type Turn = {
  turn: number;
  eou: number;
  ttft: number;
  ttfb: number;
  total: number;
  realtime: boolean;
};

const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

export function extractTurns(lines: TraceLine[]): Turn[] {
  const byTurn = new Map<number, Turn>();

  for (const line of lines) {
    if (line.event === 'turn.latency') {
      const t = num(line.turn);
      byTurn.set(t, {
        turn: t,
        eou: num(line.eou_seconds),
        ttft: num(line.ttft_seconds),
        ttfb: num(line.ttfb_seconds),
        total: num(line.total_seconds),
        realtime: false,
      });
    } else if (line.event === 'turn.realtime' || line.event === 'turn.measured_latency') {
      const t = num(line.turn);
      const existing = byTurn.get(t);
      // A real turn.latency always wins — it has the component breakdown.
      if (existing && !existing.realtime) continue;
      const total = num(line.total_seconds) || num(line.seconds) || num(line.ttft_seconds);
      byTurn.set(t, {
        turn: t,
        eou: 0,
        ttft: num(line.ttft_seconds),
        ttfb: 0,
        total,
        realtime: true,
      });
    }
  }

  return [...byTurn.values()].sort((a, b) => a.turn - b.turn);
}

export function TurnLatency({ lines }: { lines: TraceLine[] }): JSX.Element | null {
  const turns = extractTurns(lines);
  if (!turns.length) return null;

  // Scale bars against the slowest turn so the shape of the outlier is obvious.
  const max = Math.max(...turns.map((t) => t.total), 0.001);
  const anyRealtime = turns.some((t) => t.realtime);

  const p = (v: number): string => `${(v / max) * 100}%`;

  return (
    <div className="turns">
      <div className="legend">
        <span>
          <i className="legend__key turn__seg--eou" />
          EOU
        </span>
        <span>
          <i className="legend__key turn__seg--ttft" />
          LLM TTFT
        </span>
        <span>
          <i className="legend__key turn__seg--ttfb" />
          TTS TTFB
        </span>
        <span style={{ marginLeft: 'auto' }}>
          {turns.length} turn{turns.length === 1 ? '' : 's'} · slowest {max.toFixed(2)}s
          {anyRealtime ? ' · realtime pipeline (no component split)' : ''}
        </span>
      </div>

      {turns.map((t) => (
        <div className="turn" key={t.turn}>
          <span className="turn__label">#{t.turn}</span>
          <div
            className="turn__bar"
            title={
              t.realtime
                ? `turn ${t.turn}: ${t.total.toFixed(3)}s (realtime)`
                : `turn ${t.turn}: EOU ${t.eou.toFixed(3)}s · TTFT ${t.ttft.toFixed(3)}s · TTFB ${t.ttfb.toFixed(3)}s`
            }
          >
            {t.realtime ? (
              <span className="turn__seg turn__seg--ttft" style={{ width: p(t.total) }} />
            ) : (
              <>
                <span className="turn__seg turn__seg--eou" style={{ width: p(t.eou) }} />
                <span className="turn__seg turn__seg--ttft" style={{ width: p(t.ttft) }} />
                <span className="turn__seg turn__seg--ttfb" style={{ width: p(t.ttfb) }} />
              </>
            )}
          </div>
          <span className="turn__total">{t.total.toFixed(2)}s</span>
        </div>
      ))}
    </div>
  );
}
